// ─── AutoChinBot · Engine 1 — "edata" ──────────────────────────
// Data engine. Turns the raw completed-candle buffer that the AutoChinBot
// market engine (auto-chin-bot-engine.ts) already maintains into a
// structured, A-INDEXED dataset per symbol:
//
//   • candles = [A0, A1, A2, ...]
//       A0 = the most recently CLOSED H1 candle (the still-forming candle is
//       excluded), A1 = the one before it, and so on.
//   • indicator series aligned to the SAME A index (index i === A_i):
//       sma14, sma20, sma50, hma50, hma100, tma50
//
// Whenever a new candle closes, the engine recomputes the indicator series for
// that symbol in the same tick (A0 becomes the just-closed candle and every
// previous value shifts down by one index), then emits a `candleClose` event
// so the "etask" engine can run its tasks on fresh data.
//
// The engine is a thin, dependency-light layer: it only reads from the market
// engine and never touches the network.

import { SMA, HMA, TMA } from '@/lib/indicators';
import { getPipSize } from '@/lib/pip-calculator';
import type { AutoChinLogInput } from '@/lib/autochin-system-log';
import type {
  AutoChinBotEngine,
  AutoChinBotSnapshot,
  AutoChinCandle,
  AutoChinSymbolBuffer,
} from '@/lib/auto-chin-bot-engine';

// ─── Types ──────────────────────────────────────────────────────

export interface AutoChinIndicatorSeries {
  sma14: (number | null)[];
  sma20: (number | null)[];
  sma50: (number | null)[];
  hma50: (number | null)[];
  hma100: (number | null)[];
  tma50: (number | null)[];
}

export interface AutoChinEdataSymbol {
  symbolId: number;
  symbolName: string;
  digits: number;
  /** Broker pipPosition (authoritative) — null when the broker didn't provide it. */
  pipPosition: number | null;
  /** Pip size from the broker pipPosition, falling back to the asset-class heuristic. */
  pipSize: number;
  /** Completed candles, newest first. Index 0 === A0. */
  candles: AutoChinCandle[];
  /** Indicator values aligned to `candles` (index i === A_i). */
  indicators: AutoChinIndicatorSeries;
  barCount: number;
  updatedAt: number;
}

export interface EdataCandleCloseEvent {
  symbolId: number;
  symbolName: string;
  /** true on the very first dataset for a symbol (no previous A0). */
  initial: boolean;
  edata: AutoChinEdataSymbol;
}

// ─── Helpers ────────────────────────────────────────────────────

function reverse<T>(arr: T[]): T[] {
  return arr.slice().reverse();
}

/**
 * Compute every indicator series from A-indexed candles.
 * indicators.ts expects OLDEST-first input and returns an array aligned to
 * that input, so we feed oldest-first and reverse the output back to A order.
 */
function computeIndicators(candlesNewestFirst: AutoChinCandle[]): AutoChinIndicatorSeries {
  const closesOldestFirst = candlesNewestFirst.map((c) => c.close).reverse();
  return {
    sma14: reverse(SMA(closesOldestFirst, 14)),
    sma20: reverse(SMA(closesOldestFirst, 20)),
    sma50: reverse(SMA(closesOldestFirst, 50)),
    hma50: reverse(HMA(closesOldestFirst, 50)),
    hma100: reverse(HMA(closesOldestFirst, 100)),
    tma50: reverse(TMA(closesOldestFirst, 50)),
  };
}

// ─── Engine ─────────────────────────────────────────────────────

export class AutoChinEdataEngine {
  private bot: AutoChinBotEngine;
  private symbols: Record<number, AutoChinEdataSymbol> = {};
  /** Last A0 timestamp handled per symbol — used to detect a fresh close. */
  private lastA0: Record<number, number> = {};
  private unsubBot: (() => void) | null = null;
  private closeListeners = new Set<(e: EdataCandleCloseEvent) => void>();
  private logListeners = new Set<(l: AutoChinLogInput) => void>();
  private started = false;

  constructor(bot: AutoChinBotEngine) {
    this.bot = bot;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.unsubBot = this.bot.subscribe((snap) => this.onSnapshot(snap));
  }

  stop(): void {
    this.started = false;
    if (this.unsubBot) {
      this.unsubBot();
      this.unsubBot = null;
    }
  }

  /** Fires once per symbol on every NEW closed candle. */
  onCandleClose(fn: (e: EdataCandleCloseEvent) => void): () => void {
    this.closeListeners.add(fn);
    return () => { this.closeListeners.delete(fn); };
  }

  /** System-log stream (data loaded / load failed). */
  onLog(fn: (l: AutoChinLogInput) => void): () => void {
    this.logListeners.add(fn);
    return () => { this.logListeners.delete(fn); };
  }

  get(symbolId: number): AutoChinEdataSymbol | undefined {
    return this.symbols[symbolId];
  }

  getAll(): Record<number, AutoChinEdataSymbol> {
    return this.symbols;
  }

  // ─── Internal ────────────────────────────────────────────────

  private onSnapshot(snap: AutoChinBotSnapshot): void {
    for (const idStr of Object.keys(snap.symbols)) {
      const id = Number(idStr);
      const buf: AutoChinSymbolBuffer | undefined = snap.symbols[id];
      if (!buf || buf.buffer.length === 0) continue;

      const a0 = buf.buffer[0].timestamp;
      // Same A0 as last time → the forming candle is still building, nothing
      // new has closed, so there is nothing to recompute.
      if (this.lastA0[id] === a0) continue;

      const initial = this.lastA0[id] === undefined;
      this.lastA0[id] = a0;

      let edata: AutoChinEdataSymbol;
      try {
        edata = this.build(id, buf);
        this.symbols[id] = edata;

        this.emitLog({
          level: 'success',
          symbolId: id,
          symbolName: buf.symbolName,
          message: this.successMessage(buf, edata, a0, initial),
        });
      } catch (err) {
        this.emitLog({
          level: 'error',
          symbolId: id,
          symbolName: buf.symbolName,
          message:
            `LỖI nạp nến ${buf.symbolName} vào mảng: ` +
            `${err instanceof Error ? err.message : String(err)}`,
        });
        continue;
      }

      this.closeListeners.forEach((fn) => {
        try {
          fn({ symbolId: id, symbolName: buf.symbolName, initial, edata });
        } catch {
          /* a listener error must never break the engine */
        }
      });
    }
  }

  private build(symbolId: number, buf: AutoChinSymbolBuffer): AutoChinEdataSymbol {
    const candles = buf.buffer;
    return {
      symbolId,
      symbolName: buf.symbolName,
      digits: buf.digits,
      pipPosition: buf.pipPosition ?? null,
      pipSize: getPipSize(buf.symbolName, buf.pipPosition ?? null),
      candles,
      indicators: computeIndicators(candles),
      barCount: candles.length,
      updatedAt: Date.now(),
    };
  }

  private emitLog(input: AutoChinLogInput): void {
    this.logListeners.forEach((fn) => {
      try { fn(input); } catch { /* ignore */ }
    });
  }

  /**
   * Describe how much data is actually available at A0.
   * An indicator is "ready" when its value at A0 (index 0) is non-null, i.e.
   * there are enough completed candles to compute it. This is what tells the
   * user whether the loaded data is sufficient for the system to calculate.
   */
  private readinessText(edata: AutoChinEdataSymbol): string {
    const series: Array<[string, (number | null)[]]> = [
      ['SMA14', edata.indicators.sma14],
      ['SMA20', edata.indicators.sma20],
      ['SMA50', edata.indicators.sma50],
      ['HMA50', edata.indicators.hma50],
      ['HMA100', edata.indicators.hma100],
      ['TMA50', edata.indicators.tma50],
    ];
    const ready: string[] = [];
    const pending: string[] = [];
    for (const [name, arr] of series) {
      if (arr.length > 0 && arr[0] != null) ready.push(name);
      else pending.push(name);
    }
    if (pending.length === 0) {
      return `Đủ dữ liệu — 6/6 chỉ báo A0 sẵn sàng (${ready.join(', ')})`;
    }
    return (
      `Sẵn sàng ${ready.length}/6 chỉ báo A0` +
      (ready.length > 0 ? ` (${ready.join(', ')})` : '') +
      ` · chưa đủ nến cho: ${pending.join(', ')}`
    );
  }

  private successMessage(
    buf: AutoChinSymbolBuffer,
    edata: AutoChinEdataSymbol,
    a0: number,
    initial: boolean,
  ): string {
    // Derive the bar period from the A0→A1 spacing when available, so the log
    // can show the bar's CLOSE time. cTrader stamps a trendbar with its OPEN
    // time only — printing that alone made A0 look one bar behind.
    const periodMs = edata.candles.length >= 2
      ? Math.max(edata.candles[0].timestamp - edata.candles[1].timestamp, 60000)
      : 60 * 60 * 1000;
    const fmt = (ts: number): string => {
      try {
        return new Date(ts).toLocaleString('vi-VN', {
          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });
      } catch { return String(ts); }
    };
    const head = initial ? 'Nạp dữ liệu ban đầu' : 'Nạp nến mới vào mảng';
    return (
      `${head} thành công: ${buf.symbolName} · A0 mở ${fmt(a0)} → đóng ${fmt(a0 + periodMs)} · ${edata.barCount} nến` +
      ` — ${this.readinessText(edata)}`
    );
  }
}